import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import {
  AccountingActionCategory,
  AccountingActionLabel,
  ActionCategoryMapping,
  CUSTOMER_MULTI_SELECT_ACTIONS,
  CustomerAccountingActionAllowedTypes,
  SUPPLIER_MULTI_SELECT_ACTIONS,
  SupplierAccountingActionAllowedTypes,
} from "@/features/chart-of-account-preferences";
import { ChartOfAccountSelect } from "@/features/chart-of-accounts";
import { CompanySelect } from "@/features/companies";
import { SellerBankAccountsSection } from "@/features/seller-bank-accounts";
import { StatusEnum, statusLabels } from "@/features/shared";
import {
  CenteredLoading,
  CustomDialogTitle,
  CustomInputField,
  CustomSelect,
} from "@/features/ui";

import { OutletRoleEnum, OutletRoleSelect } from "../outlet-role";
import { DEFAULT_VALUES, VALIDATION_SCHEMA } from "./outlet-form-constants";

const CUSTOMER_ACTIONS =
  ActionCategoryMapping[AccountingActionCategory.CUSTOMER];
const SUPPLIER_ACTIONS =
  ActionCategoryMapping[AccountingActionCategory.SUPPLIER];

export const OutletFormDialog = ({
  defaultValues,
  onSubmit,
  onClose,
  dialogTitle,
  renderDialogActionsChildren,
  isLoading = false,
}) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, errors },
  } = methods;

  const [tabIndex, setTabIndex] = useState(0);
  const role = watch("role");
  const isRetail = role === OutletRoleEnum.RETAIL;
  const isHub = role === OutletRoleEnum.HUB;
  const hasAccountTab = isRetail || isHub;
  const accountActions = isRetail ? CUSTOMER_ACTIONS : SUPPLIER_ACTIONS;
  const accountMultiSelect = isRetail
    ? CUSTOMER_MULTI_SELECT_ACTIONS
    : SUPPLIER_MULTI_SELECT_ACTIONS;
  const accountAllowedTypes = isRetail
    ? CustomerAccountingActionAllowedTypes
    : SupplierAccountingActionAllowedTypes;

  useEffect(() => {
    if (defaultValues) reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!hasAccountTab && tabIndex === 1) {
      setTabIndex(0);
    }
    if (!isHub && tabIndex === 2) {
      setTabIndex(0);
    }
  }, [hasAccountTab, isHub, tabIndex]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = async (formData) => {
    const accountingActionChartOfAccounts = hasAccountTab
      ? Object.entries(formData.accountingActionChartOfAccounts || {}).flatMap(
          ([action, value]) => {
            if (!accountActions.includes(action)) return [];
            if (accountMultiSelect.has(action)) {
              return (value || []).map((coa) => ({
                action,
                chartOfAccountId: coa.id,
              }));
            }
            return value ? [{ action, chartOfAccountId: value.id }] : [];
          },
        )
      : [];

    const sellerBankAccounts = isHub
      ? (formData.sellerBankAccounts || []).map((sellerBankAccount) => ({
          accountHolder: sellerBankAccount.accountHolder,
          accountNumber: sellerBankAccount.accountNumber,
          bankId: sellerBankAccount.bank.id,
        }))
      : undefined;

    await onSubmit({
      ...formData,
      companyId: formData.company?.id ?? null,
      accountingActionChartOfAccounts,
      sellerBankAccounts,
    });
  };

  return (
    <FormProvider {...methods}>
    <Dialog
      open
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      component="form"
      onSubmit={handleSubmit(onFormSubmit)}
    >
      <CustomDialogTitle
        variant="h5"
        title={dialogTitle}
        onClose={handleClose}
      />
      {isLoading ? (
        <CenteredLoading sx={{ height: "25rem" }} />
      ) : (
        <>
          <DialogContent sx={{ pt: 0 }} dividers>
            <Tabs
              value={tabIndex}
              onChange={(_, newValue) => setTabIndex(newValue)}
            >
              <Tab label="Outlet" />
              {hasAccountTab && (
                <Tab label={isRetail ? "Akun Penjualan" : "Akun Pembelian"} />
              )}
              {isHub && <Tab label="Rekening Bank" />}
            </Tabs>
            <Divider sx={{ mb: 2 }} />

            {tabIndex === 0 && (
              <Stack spacing={2}>
                <CustomInputField
                  required
                  control={control}
                  name="name"
                  label="Nama"
                  register={register}
                  helperText={errors.name?.message}
                  error={Boolean(errors.name)}
                />
                <CustomInputField
                  required
                  control={control}
                  name="address"
                  label="Alamat"
                  register={register}
                  InputProps={{ multiline: true }}
                  helperText={errors.address?.message}
                  error={Boolean(errors.address)}
                />
                <CustomInputField
                  required
                  control={control}
                  name="whatsapp"
                  label="Whatsapp"
                  register={register}
                  InputProps={{ placeholder: "6281xxxx" }}
                  helperText={errors.whatsapp?.message}
                  error={Boolean(errors.whatsapp)}
                />
                <Controller
                  control={control}
                  name="role"
                  render={({ field, fieldState }) => (
                    <OutletRoleSelect
                      required
                      value={field.value}
                      onChange={field.onChange}
                      label="Peran"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="status"
                  render={({ field, fieldState }) => (
                    <CustomSelect
                      required
                      value={field.value}
                      onChange={field.onChange}
                      id="status"
                      label="Status"
                      options={Object.values(StatusEnum).map((value) => ({
                        value: value,
                        label: statusLabels[value],
                      }))}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="company"
                  render={({ field, fieldState }) => (
                    <CompanySelect
                      value={field.value}
                      onChange={field.onChange}
                      TextFieldProps={{
                        label: "Perusahaan",
                        error: fieldState.invalid,
                        helperText: fieldState.error?.message,
                        InputProps: {
                          placeholder: "Pilih Perusahaan",
                        },
                      }}
                    />
                  )}
                />
              </Stack>
            )}

            {hasAccountTab && tabIndex === 1 && (
              <Stack spacing={3}>
                {accountActions.map((action) => {
                  const isMultiple = accountMultiSelect.has(action);
                  return (
                    <Box key={action}>
                      <Typography variant="subtitle1" gutterBottom>
                        {AccountingActionLabel[action]}
                      </Typography>
                      <Controller
                        control={control}
                        name={`accountingActionChartOfAccounts.${action}`}
                        render={({ field }) => (
                          <ChartOfAccountSelect
                            multiple={isMultiple}
                            types={accountAllowedTypes[action]}
                            value={
                              isMultiple
                                ? field.value || []
                                : field.value || null
                            }
                            onChange={(value) =>
                              field.onChange(
                                isMultiple ? value : value || null,
                              )
                            }
                            TextFieldProps={{
                              placeholder: "Cari dan pilih akun...",
                            }}
                          />
                        )}
                      />
                    </Box>
                  );
                })}
              </Stack>
            )}

            {isHub && tabIndex === 2 && <SellerBankAccountsSection />}
          </DialogContent>
          <DialogActions>
            {renderDialogActionsChildren({ isLoading: isSubmitting })}
          </DialogActions>
        </>
      )}
    </Dialog>
    </FormProvider>
  );
};

OutletFormDialog.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  renderDialogActionsChildren: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
